import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Star } from 'lucide-react';
import { Button } from './ui/button';
import { siteConfig } from '../mockData';
import { useLanguage } from '../LanguageContext';
import { useSiteStats } from '../SiteStatsContext';
import { AnimateOnScroll } from '../hooks/useScrollAnimation';

const MainCTA = () => {
  const { t } = useLanguage();
  const stats = useSiteStats();
  const navigate = useNavigate();

  return (
    <section className="py-10 md:py-20 px-4 md:px-8">
      <div className="max-w-4xl mx-auto text-center">
        {/* Logo */}
        <AnimateOnScroll animation="scale-up" className="mb-6 md:mb-8">
          <img 
            src="/aqmopti-logo.png" 
            alt="AqmOpti" 
            className="w-20 h-20 md:w-32 md:h-32 mx-auto opacity-90 rounded-2xl animate-float"
            style={{ mixBlendMode: 'lighten' }}
          />
        </AnimateOnScroll>

        {/* Badge */}
        <AnimateOnScroll animation="fade-down" delay={100}>
          <span className="inline-block px-3 md:px-4 py-1 md:py-1.5 rounded-full bg-cyan-950/50 border border-cyan-800/50 text-cyan-400 text-[10px] md:text-xs font-semibold uppercase tracking-wider mb-4 md:mb-6">
            {t('cta.badge')}
          </span>
        </AnimateOnScroll>

        {/* Heading */}
        <AnimateOnScroll animation="fade-up" delay={200}>
          <h2 className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-2 md:mb-4">
            {t('cta.title1')}
          </h2>
          <h2 className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-bold mb-4 md:mb-8">
            <span className="bg-gradient-to-r from-cyan-500 to-cyan-700 bg-clip-text text-transparent">
              {t('cta.title2')}
            </span>
          </h2>
        </AnimateOnScroll>

        {/* Description */}
        <AnimateOnScroll animation="fade-up" delay={300}>
          <p className="text-gray-400 text-sm md:text-lg mb-6 md:mb-10 max-w-2xl mx-auto leading-relaxed px-2">
            {t('cta.description')}
          </p>
        </AnimateOnScroll>

        {/* CTA Buttons */}
        <AnimateOnScroll animation="fade-up" delay={400} className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4 mb-8 md:mb-12">
          <Button 
            onClick={() => navigate('/services')}
            className="w-full sm:w-auto bg-cyan-600 hover:bg-cyan-700 text-white font-semibold px-6 md:px-8 py-3 md:py-6 text-sm md:text-base rounded-xl transition-all duration-200 group btn-glow"
          >
            {t('cta.discoverServices')}
            <ArrowRight className="ml-2 w-4 h-4 md:w-5 md:h-5 group-hover:translate-x-1 transition-transform" />
          </Button>
          <Button 
            onClick={() => navigate('/feedbacks')}
            variant="outline" 
            className="w-full sm:w-auto border-cyan-600 text-cyan-500 hover:bg-cyan-950/30 font-semibold px-6 md:px-8 py-3 md:py-6 text-sm md:text-base rounded-xl transition-all duration-200 hover-lift"
          >
            {t('cta.seeReviews')}
            <ArrowRight className="ml-2 w-4 h-4 md:w-5 md:h-5" />
          </Button>
        </AnimateOnScroll>

        {/* Rating */}
        <AnimateOnScroll animation="fade-up" delay={500} className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-3">
          <div className="flex items-center gap-1">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-4 h-4 md:w-5 md:h-5 fill-yellow-500 text-yellow-500" />
            ))}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-white font-bold text-base md:text-lg">{stats.rating}/5</span>
            <span className="text-gray-400 text-xs md:text-sm">
              <span className="text-cyan-500 font-semibold">+{stats.optimizedPCs}</span> {t('cta.optimizedPCs')}
            </span>
          </div>
        </AnimateOnScroll>
      </div>
    </section>
  );
};

export default MainCTA;
